<script>
  import { onMount } from "svelte";
  import { Router, Route, navigate } from "svelte-routing";
  import Nav from "./Nav.svelte";
  import Home from "./routes/Home.svelte";
  import AddPromotion from "./routes/AddPromotion.svelte";
  import Search from "./routes/Search.svelte";
  import EditPromotion from "./routes/EditPromotion.svelte";
  import TodaySpecials from "./routes/TodaySpecials.svelte";
  import Profile from "./routes/Profile.svelte";
  import Favourites from "./routes/Favourites.svelte";
  import MyAchievements from "./routes/MyAchievements.svelte";
  import AchievementDetail from "./routes/AchievementDetail.svelte";
  import Memberships from "./routes/Memberships.svelte";
  import Payment from "./routes/Payment.svelte";
  import BusinessPlans from "./routes/BusinessPlans.svelte";
  import BizPayment from "./routes/BizPayment.svelte";
  
  // Admin imports
  import AdminSubscriptions from "./routes/AdminSubscriptions.svelte";
  import BankMaintenance from "./routes/BankMaintenance.svelte";
  import CreditCardMaintenance from "./routes/CreditCardMaintenance.svelte";
  import UserManagement from "./routes/UserManagement.svelte";
  import UserDetailAdmin from "./routes/UserDetailAdmin.svelte";
  import PaymentMethodMaintenance from "./routes/PaymentMethodMaintenance.svelte";
  import QuestionMaintenance from "./routes/QuestionMaintenance.svelte";
  import CategoryMaintenance from "./routes/CategoryMaintenance.svelte";
  import AdminPremiseInvite from "./routes/AdminPremiseInvite.svelte";
  import StoreMaintenance from "./routes/StoreMaintenance.svelte";
  import PlaceMaintenance from "./routes/PlaceMaintenance.svelte";
  import AdminMessages from "./routes/AdminMessages.svelte";
  import AdminReports from "./routes/AdminReports.svelte";
  import BusinessPlanMaintenance from "./routes/BusinessPlanMaintenance.svelte";
  import AdminQASettings from "./routes/AdminQASettings.svelte";
  import AdminMarketplace from "./routes/AdminMarketplace.svelte";
  import AdminPromotions from "./routes/AdminPromotions.svelte";
  import AdminBannedPromotions from "./routes/AdminBannedPromotions.svelte";
  import AdminMissingFormulas from "./routes/AdminMissingFormulas.svelte";
  import AdminBroadcast from "./routes/AdminBroadcast.svelte";
  import AdminBanners from "./routes/AdminBanners.svelte";
  import AdminReceiptVerification from "./routes/AdminReceiptVerification.svelte";
  import AdminCampaignPoints from "./routes/AdminCampaignPoints.svelte";
  import AdminRedeemableItems from "./routes/AdminRedeemableItems.svelte";
  import AdminSystemSettings from "./routes/AdminSystemSettings.svelte";
  import AdminCronJobs from "./routes/AdminCronJobs.svelte";

  // Business imports
  import PremiseManagement from "./routes/PremiseManagement.svelte";
  import MyPromotions from "./routes/MyPromotions.svelte";
  import AddSimplePromotion from "./routes/AddSimplePromotion.svelte";
  import TargetAudience from "./routes/TargetAudience.svelte";
  import BusinessProfile from "./routes/BusinessProfile.svelte";
  
  // User/Public imports
  import MyInbox from "./routes/MyInbox.svelte";
  import PromotionDetail from "./routes/PromotionDetail.svelte";
  import ScanProduct from "./routes/ScanProduct.svelte";
  import ScanBulkProducts from "./routes/ScanBulkProducts.svelte";
  import AddProduct from "./routes/AddProduct.svelte";
  import EditProduct from "./routes/EditProduct.svelte";
  import SearchProducts from "./routes/SearchProducts.svelte";
  import ScanPromotion from "./routes/ScanPromotion.svelte";
  import ClaimPremise from "./routes/ClaimPremise.svelte";
  import TopSavers from "./routes/TopSavers.svelte";
  import MyDashboard from "./routes/MyDashboard.svelte";
  import Donate from "./routes/Donate.svelte";
  import DonationList from "./routes/DonationList.svelte";
  import ContactUs from "./routes/ContactUs.svelte";
  import TaxCalculator from "./routes/TaxCalculator.svelte";
  import OpenAccountCalculator from "./routes/OpenAccountCalculator.svelte";
  import HandyTools from "./routes/HandyTools.svelte";
  
  // Q&A Imports
  import QAList from "./routes/QAList.svelte";
  import AskQuestion from "./routes/AskQuestion.svelte";
  import QuestionDetail from "./routes/QuestionDetail.svelte";

  // Marketplace Imports
  import Marketplace from "./routes/Marketplace.svelte";
  import SellItem from "./routes/SellItem.svelte";
  import CreditTopUp from "./routes/CreditTopUp.svelte";

  // Legal Imports
  import TermsOfService from "./routes/TermsOfService.svelte";
  import PrivacyPolicy from "./routes/PrivacyPolicy.svelte";

  import ProtectedRoute from "./components/ProtectedRoute.svelte";
  import Notification from './components/Notification.svelte';
  import LoginPromptModal from './components/LoginPromptModal.svelte';

  import AdminScraper from "./routes/AdminScraper.svelte";
  import AdminAIModels from "./routes/AdminAIModels.svelte";
  import AdminAITaskConfigs from "./routes/AdminAITaskConfigs.svelte";

  import BillAnalyzer from "./routes/BillAnalyzer.svelte";
  import AdminLogs from "./routes/AdminLogs.svelte";
  import AdminRecommendationLogs from "./routes/AdminRecommendationLogs.svelte";
  import AdminSearchLogs from "./routes/AdminSearchLogs.svelte";

  import PartnerPage from './routes/PartnerPage.svelte';
  import PartnerPromoDetail from './routes/PartnerPromoDetail.svelte';

  import CardHolders from "./routes/CardHolders.svelte";
  import PublicProfile from "./routes/PublicProfile.svelte";
  import ERDViewer from "./routes/ERDViewer.svelte";



  import { authStore } from "./authStore";
  import { initializeMsal } from "./authService";
  import { authFetch } from "./authService";
  import { initializePusher } from './pusherService.js';
  import { initializePostHog } from './posthog.js';
  import { joinedChallenges } from './achievementStore.js';
  import { getApiUrl } from "./api";
  let isAuthReady = false;

  onMount(async () => {
    // 1. Initialize Authentication
    await initializeMsal();
    isAuthReady = true;
    
    // 2. Initialize Real-time Service (Pusher)
    // The service now handles the 'new' keyword internally to avoid compiler errors
    initializePusher();

    initializePostHog();
  });

  // Redirect new users to profile creation if they haven't set a display name
  $: {
    if (isAuthReady) {
      const { user, profile } = $authStore;
      const currentPath = window.location.pathname;

      if (user && !profile && currentPath !== '/profile') {
        navigate('/profile', { replace: true });
      }

      // --- NEW: Load Joined Challenges ---
      // if (user) {
      //   console.log("user is not null.");
      // }
      // if (profile) {
      //   console.log("profile is not null.");
      // }
      if (user && profile) {
          loadAchievements(user.idTokenClaims.oid);
      }
    }
  }

  async function loadAchievements(userId) {
    //console.log("3. loadAchievements function entered");
      try {
          // Use authFetch to get user's achievements
          const res = await authFetch(getApiUrl(`/achievements/user/${userId}`));
          if (res.ok) {
              const list = await res.json();
              const map = {};
              list.forEach(a => {
                  // Only map active or completed (not expired, unless you want to log past ones?)
                  // Usually logging is for active.
                  const isExpired = new Date(a.targetCompletionDate) < new Date();
                  if (!isExpired) {
                      map[a.promotionId] = a._id;
                  }
              });
              joinedChallenges.set(map);
          }
      } catch (e) {
          console.error("Failed to sync achievements:", e);
      }
  }
</script>

<Router>
  {#if isAuthReady}
    <Nav />
    <main>
      <!-- Public Routes -->
      <Route path="/search" component={Search} />
      <Route path="/promotions/detail/:id" component={PromotionDetail} />
      <Route path="/today-special" component={TodaySpecials} />
      <Route path="/products/search" component={SearchProducts} />
      <Route path="/top-savers" component={TopSavers} />
      <Route path="/claim" component={ClaimPremise} />
      <Route path="/donate" component={Donate} />
      <Route path="/donations/list" component={DonationList} />
      <Route path="/contact" component={ContactUs} />
      
      <!-- Legal Routes -->
      <Route path="/terms" component={TermsOfService} />
      <Route path="/privacy" component={PrivacyPolicy} />
      
      <!-- Marketplace (Public View) -->
      <Route path="/marketplace" component={Marketplace} /> 

      <!-- Q&A (Public List) -->
      <Route path="/qa/list" component={QAList} />
      
      <Route path="/" component={Home} />

      <Route path="/tax-calculator" component={TaxCalculator} />
      <Route path="/open-account-calculator" component={OpenAccountCalculator} />
      <Route path="/tools" component={HandyTools} />
      <Route path="/handy-tools" component={HandyTools} />
      <Route path="/bill-analyzer" component={BillAnalyzer} />
      <Route path="/dev-erd" component={ERDViewer} />

      <!-- === PARTNER ROUTES (I.T Fashion) === -->
      <Route path="/it">
        <PartnerPage brand="it" brandDisplayName="I.T Fashion" />
      </Route>
      
      <!-- FIX: Make sure the path exactly matches the Link in PartnerPage -->
      <Route path="/it/promo/:id" let:params>
        <PartnerPromoDetail id={params.id} />
      </Route>

      <Route path="/cardholders/:cardName" component={CardHolders} />
      <Route path="/user/public/:id" component={PublicProfile} />
      
      <!-- Protected Routes for ANY logged-in user -->
      <ProtectedRoute path="/dashboard" component={MyDashboard} />
      <ProtectedRoute path="/profile" component={Profile} />
      <ProtectedRoute path="/favourites/:userId" component={Favourites} />
      <ProtectedRoute path="/achievements/:userId" component={MyAchievements} />
      <ProtectedRoute path="/achievements/detail/:id" component={AchievementDetail} />
      <ProtectedRoute path="/memberships" component={Memberships} />
      <ProtectedRoute path="/payment/:id" component={Payment} />
      <ProtectedRoute path="/business-plans" component={BusinessPlans} />
      <ProtectedRoute path="/biz-payment/:id" component={BizPayment} />
      <ProtectedRoute path="/add" component={AddPromotion} />
      <ProtectedRoute path="/edit/:id" component={EditPromotion} />
      <ProtectedRoute path="/promotions/user/:userId" component={MyPromotions} />
      <ProtectedRoute path="/inbox/:userId" component={MyInbox} />
      <ProtectedRoute path="/scan" component={ScanPromotion} />
      <ProtectedRoute path="/products/scan" component={ScanProduct} />
      <ProtectedRoute path="/products/bulk-scan" component={ScanBulkProducts} isBusinessRoute={true} />
      <ProtectedRoute path="/products/add" component={AddProduct} />
      <ProtectedRoute path="/products/edit/:id" component={EditProduct} />
      
      <!-- Protected Q&A Actions -->
      <ProtectedRoute path="/qa/ask" component={AskQuestion} />
      <Route path="/qa/questions/:id" component={QuestionDetail} />

      <!-- Protected Marketplace Actions -->
      <ProtectedRoute path="/marketplace/sell" component={SellItem} />
      <ProtectedRoute path="/credits/topup" component={CreditTopUp} />

      <!-- Admin Routes -->
      <ProtectedRoute path="/admin/subscriptions" component={AdminSubscriptions} isAdminRoute={true} />
      <ProtectedRoute path="/admin/users" component={UserManagement} isAdminRoute={true} />
      <ProtectedRoute path="/admin/users/:userId" component={UserDetailAdmin} isAdminRoute={true} />
      <ProtectedRoute path="/admin/banks" component={BankMaintenance} isAdminRoute={true} />
      <ProtectedRoute path="/admin/credit-cards" component={CreditCardMaintenance} isAdminRoute={true} />
      <ProtectedRoute path="/admin/payment-methods" component={PaymentMethodMaintenance} isAdminRoute={true} />
      <ProtectedRoute path="/admin/questions" component={QuestionMaintenance} isAdminRoute={true} />
      <ProtectedRoute path="/admin/categories" component={CategoryMaintenance} isAdminRoute={true} />
      <ProtectedRoute path="/admin/stores" component={StoreMaintenance} isAdminRoute={true} />
      <ProtectedRoute path="/admin/places" component={PlaceMaintenance} isAdminRoute={true} />
      <ProtectedRoute path="/admin/invite" component={AdminPremiseInvite} isAdminRoute={true} />
      <ProtectedRoute path="/admin/messages" component={AdminMessages} isAdminRoute={true} />
      <ProtectedRoute path="/admin/reports" component={AdminReports} isAdminRoute={true} />
      <ProtectedRoute path="/admin/business-plans" component={BusinessPlanMaintenance} isAdminRoute={true} />
      <ProtectedRoute path="/admin/qa" component={AdminQASettings} isAdminRoute={true} />
      <ProtectedRoute path="/admin/marketplace" component={AdminMarketplace} isAdminRoute={true} />
      <ProtectedRoute path="/admin/promotions" component={AdminPromotions} isAdminRoute={true} /> <!-- Imported in route definition but file should exist -->
      <ProtectedRoute path="/admin/banned-promotions" component={AdminBannedPromotions} isAdminRoute={true} />
      <ProtectedRoute path="/admin/scraper" component={AdminScraper} isAdminRoute={true} />
      <ProtectedRoute path="/admin/formulas/missing" component={AdminMissingFormulas} isAdminRoute={true} />
      <ProtectedRoute path="/admin/broadcast" component={AdminBroadcast} isAdminRoute={true} />

      <ProtectedRoute path="/admin/logs" component={AdminLogs} isAdminRoute={true} />
      <ProtectedRoute path="/admin/ai-models" component={AdminAIModels} isAdminRoute={true} />
      <ProtectedRoute path="/admin/ai-tasks" component={AdminAITaskConfigs} isAdminRoute={true} />
      <ProtectedRoute path="/admin/recommendation-logs" component={AdminRecommendationLogs} isAdminRoute={true} />
      <ProtectedRoute path="/admin/search-logs" component={AdminSearchLogs} isAdminRoute={true} />
      <ProtectedRoute path="/admin/banners" component={AdminBanners} isAdminRoute={true} />
      <ProtectedRoute path="/admin/receipt-verification" component={AdminReceiptVerification} isAdminRoute={true} />
      <ProtectedRoute path="/admin/campaign" component={AdminCampaignPoints} isAdminRoute={true} />
      <ProtectedRoute path="/admin/rewards" component={AdminRedeemableItems} isAdminRoute={true} />
      <ProtectedRoute path="/admin/system-settings" component={AdminSystemSettings} isAdminRoute={true} />
      <ProtectedRoute path="/admin/cron-jobs" component={AdminCronJobs} isAdminRoute={true} />
      
      <!-- Protected Routes for BUSINESS users ONLY -->
      <ProtectedRoute path="/premises/management" component={PremiseManagement} isBusinessRoute={true} />
      <ProtectedRoute path="/promotions/add-simple" component={AddSimplePromotion} isBusinessRoute={true} />
      <ProtectedRoute path="/audience" component={TargetAudience} isBusinessRoute={true} />
      <ProtectedRoute path="/business-profile" component={BusinessProfile} isBusinessRoute={true} />
      
    </main>
  {:else}
    <div class="loading-container">
      <p>Initializing authentication, please wait...</p>
    </div>
  {/if}
</Router>

<Notification />
<LoginPromptModal />

<style>
  :global(body) {
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    background-color: #f8f9fa;
  }

  main {
    text-align: center;
    padding: 1em;
    max-width: 1200px;
    margin: 0 auto;
  }

  @media (max-width: 640px) {
    main {
      padding: 0.5rem 0.25rem;
    }
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    font-size: 1.2rem;
    color: #555;
  }
</style>